import DinesLink from "@/components/library/Misc/DinesLink";
import {
  type ISidebarLink,
  SidebarLinkType,
  SidebarSection,
} from "@/types/layout/navigation";
import { cn } from "@/utils/css";
import SideBarItem from "./sidebar-item";

interface IMainSideBarLinks {
  links: ISidebarLink[];
  selected: string;
  collapseSidebar: boolean;
}

const SECTION_ORDER: SidebarSection[] = [
  SidebarSection.Manage,
  SidebarSection.Operate,
  SidebarSection.System,
];

const SECTION_LABELS: Record<SidebarSection, string> = {
  [SidebarSection.Operate]: "Operate",
  [SidebarSection.Manage]: "Manage",
  [SidebarSection.System]: "System",
};

const SECTION_DIVIDER_CLASSES = "mt-4 border-t border-white/10 pt-4";

const renderEntry = (
  item: ISidebarLink,
  selected: string,
  collapseSidebar: boolean,
) => {
  const sideBarItem = (
    <SideBarItem
      collapseSidebar={collapseSidebar}
      selected={selected}
      item={item}
    />
  );

  if (item.type === SidebarLinkType.Action) {
    return (
      <button
        type="button"
        className="w-full text-left"
        onClick={() => item.action?.()}
      >
        {sideBarItem}
      </button>
    );
  }

  return (
    <DinesLink
      href={item.url ?? ""}
      className="flex w-full"
      {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {sideBarItem}
    </DinesLink>
  );
};

const MainSideBarLinks: React.FC<IMainSideBarLinks> = ({
  links,
  selected,
  collapseSidebar,
}) => {
  const groups = SECTION_ORDER.map((section) => ({
    section,
    items: links.filter((link) => link.section === section),
  })).filter((group) => group.items.length > 0);

  return (
    <div className={cn("grow px-4 pt-6 pb-2", collapseSidebar && "md:px-2")}>
      {groups.map((group, index) => (
        <div
          key={group.section}
          className={cn(index > 0 && SECTION_DIVIDER_CLASSES)}
        >
          <div
            className={cn(
              "mb-2 px-4 font-semibold text-gray-500 text-xs uppercase tracking-widest",
              collapseSidebar && "md:px-0 md:text-center",
            )}
          >
            {SECTION_LABELS[group.section]}
          </div>
          {group.items.map((item) => (
            <div
              key={item.id}
              id={`sidebar-${item.id}`}
              data-testid={`sidebar-${item.id}`}
              data-active={selected === item.id}
              className={cn(item.id === "logout" && SECTION_DIVIDER_CLASSES)}
            >
              {renderEntry(item, selected, collapseSidebar)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default MainSideBarLinks;
